import { ActionTypes } from 'further-types/lp/action';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  IncomeOrExpenseApportionmentMode,
  IncomeOrExpenseState,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { FundingSource } from 'further-types/lp/investment';
import { LpAccrualFrequency } from 'further-types/lp/lp-fee-template';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { z } from 'zod';
import { CalendarDay } from '../../../utils';
import { isMongoId, withPaginationParams } from '../../helpers';
import { allocationMatchesCheckAmount } from '../../helpers/transaction-allocations-input';
import { CreateAllocation } from '../allocations';
import { IndividualTransaction } from '../transaction';

const sharedFieldsCreateIncomeOrExpense = {
  vehicleId: z.string().refine(isMongoId()),
  counterparty: z.object(
    {
      id: z
        .string()
        .optional()
        .refine(isMongoId({ optional: true })),
      name: z.string().min(1, {
        message: 'Please enter a counterparty name.',
      }),
    },
    {
      message: 'Please enter a counterparty name.',
    },
  ),
  sourceAllocationActionId: z
    .string()
    .optional()
    .nullable()
    .refine(isMongoId({ optional: true })),
  category: z.nativeEnum(IncomeOrExpenseType, {
    message: 'Please select the income or expense type.',
  }),
  incomeOrExpenseType: z.string({
    message: 'Please select the income or expense type.',
  }),
  incomeOrExpenseState: z.nativeEnum(IncomeOrExpenseState, {
    message: 'Please select the income or expense state.',
  }),
  fundingSource: z.nativeEnum(FundingSource).optional(),
  currencyCode: z.nativeEnum(CurrencyCode, {
    message: 'Please select a valid currency.',
  }),
  amountExVatTcy: z.number({
    required_error: 'Please enter an amount.',
    invalid_type_error: 'Please enter a valid amount.',
  }),
  vatRate: z
    .number({
      required_error: 'Please enter a valid VAT rate.',
      invalid_type_error: 'Please enter a valid VAT rate.',
    })
    .min(0)
    .max(100),
  amountIncVatTcy: z.number(),
  invoiceFile: z
    .object({
      path: z.string(),
      name: z.string(),
      preview: z.string(),
    })
    .optional()
    .nullable(),
  description: z.string().optional(),
  allocationRule: z.nativeEnum(AllocationRule),
  allocations: z.array(CreateAllocation, {
    message: 'Please allocate the value.',
  }),
  apportionmentPeriods: z.array(
    z.object({
      id: z.string().optional(),
      transactions: z.array(
        IndividualTransaction.extend({
          sourceActionId: z.string().optional(),
          transactionDate: z.instanceof(CalendarDay).optional(),
        }),
      ),
      currencyRate: z
        .object({
          fetchedRate: z.number().optional(),
          fetchedSource: z.nativeEnum(FxDataFeed).optional(),
          overrideRate: z.number().optional(),
          overrideSource: z.string().optional(),
        })
        .optional(),
    }),
  ),
};

const singleLedgerDateMode = {
  apportionmentMode: z.literal(IncomeOrExpenseApportionmentMode.Single),
  startDate: z.instanceof(CalendarDay, {
    message: 'Please select a start date.',
  }),
  endDate: z.instanceof(CalendarDay).optional(),
  ...sharedFieldsCreateIncomeOrExpense,
};

const multipleLedgerDateMode = {
  apportionmentMode: z.literal(IncomeOrExpenseApportionmentMode.Multiple),
  startDate: z.instanceof(CalendarDay, {
    message: 'Please select a start date.',
  }),
  endDate: z.instanceof(CalendarDay, {
    message: 'Please select an end date.',
  }),
  apportionmentBasis: z.nativeEnum(LpAccrualFrequency, {
    message: 'Please select an apportionment basis.',
  }),
  ...sharedFieldsCreateIncomeOrExpense,
};

export const CreateIncomeOrExpense = z
  .discriminatedUnion('apportionmentMode', [
    z.object(singleLedgerDateMode),
    z.object(multipleLedgerDateMode),
  ])

  .refine(
    (data) =>
      allocationMatchesCheckAmount(data.amountIncVatTcy, data.allocations),
    {
      message: 'Total allocation amount must match input amount.',
      path: ['allocations'],
    },
  )
  .refine(
    (data) => {
      if (
        data.category === IncomeOrExpenseType.Expense &&
        data.incomeOrExpenseState !== IncomeOrExpenseState.Accrued
      ) {
        return data.fundingSource !== undefined;
      }
      return true;
    },
    {
      message: 'Funding source is required.',
      path: ['fundingSource'],
    },
  );

export const UpdateIncomeOrExpense = z
  .discriminatedUnion('apportionmentMode', [
    z.object({
      id: z.string().refine(isMongoId()),
      ...singleLedgerDateMode,
    }),
    z.object({
      id: z.string().refine(isMongoId()),
      ...multipleLedgerDateMode,
    }),
  ])
  .refine(
    (data) =>
      allocationMatchesCheckAmount(data.amountIncVatTcy, data.allocations),
    {
      message: 'Total allocation amount must match input amount.',
      path: ['allocations'],
    },
  )
  .refine(
    (data) => {
      if (
        data.category === IncomeOrExpenseType.Expense &&
        data.incomeOrExpenseState !== IncomeOrExpenseState.Accrued
      ) {
        return data.fundingSource !== undefined;
      }
      return true;
    },
    {
      message: 'Funding source is required.',
      path: ['fundingSource'],
    },
  );

export const ApportionmentPeriods = z.object({
  apportionmentBasis: z.nativeEnum(LpAccrualFrequency),
  apportionmentStartDate: z.instanceof(CalendarDay),
  apportionmentEndDate: z.instanceof(CalendarDay),
  amountIncVatTcy: z.number(),
  amountExVatTcy: z.number(),
  currency: z.nativeEnum(CurrencyCode),
  allocations: z.array(CreateAllocation),
});

export const ListIncomeOrExpenses = withPaginationParams(
  z.object({
    actionType: z.nativeEnum(IncomeOrExpenseType).optional(),
    searchQuery: z.string().optional(),
    startDate: z.instanceof(CalendarDay).optional(),
    endDate: z.instanceof(CalendarDay).optional(),
  }),
  [
    'startDate',
    'createdDate',
    'amount',
    'counterparty',
    'status',
    'actionType',
  ],
);

export const GetAccruedIncomeOrExpenses = withPaginationParams(
  z.object({
    category: z.nativeEnum(IncomeOrExpenseType),
    viewDate: z.instanceof(CalendarDay).optional(),
    periodStart: z.instanceof(CalendarDay).optional(),
    periodEnd: z.instanceof(CalendarDay).optional(),
  }),
  ['transactionDate'],
);

export const GetIncomeAndExpensesTransactions = z.object({
  actionType: z.nativeEnum(ActionTypes),
  customActionTypeId: z
    .string()
    .optional()
    .nullable()
    .refine(isMongoId({ optional: true })),
  incomeOrExpenseGroupId: z
    .string()
    .optional()
    .nullable()
    .refine(isMongoId({ optional: true })),
  incomeOrExpenseState: z.nativeEnum(IncomeOrExpenseState),
  category: z.nativeEnum(IncomeOrExpenseType),
  amountIncVatTcy: z.number(),
  amountExVatTcy: z.number(),
  currency: z.nativeEnum(CurrencyCode),
  transactions: z.array(IndividualTransaction).optional(),
  isSwitchOperation: z.boolean().optional(),
  switchDate: z.instanceof(CalendarDay).optional(),
});

export const SwitchIncomeOrExpense = z.object({
  vehicleId: z.string().refine(isMongoId()),
  id: z.string().refine(isMongoId()),
  switchDate: z.instanceof(CalendarDay, {
    message: 'Please select a payable ledger date.',
  }),
  counterparty: sharedFieldsCreateIncomeOrExpense.counterparty,
  incomeOrExpenseType: sharedFieldsCreateIncomeOrExpense.incomeOrExpenseType,
  allocations: sharedFieldsCreateIncomeOrExpense.allocations,
  fundingSource: z.nativeEnum(FundingSource).optional(),
  vatRate: z.number().optional(),
  amountExVatTcy: z.number().optional(),
  description: z.string().optional(),
  invoiceFile: z
    .object({
      path: z.string(),
      name: z.string(),
      preview: z.string(),
    })
    .optional()
    .nullable(),
  apportionmentPeriods: sharedFieldsCreateIncomeOrExpense.apportionmentPeriods,
});
